import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner } from '../components/UIComponents';
import { dashboardAPI } from '../utils/api';
import { formatNumber, formatDecimal, formatCurrency } from '../utils/helpers';
import ActiveAlarmsPanel from '../components/ActiveAlarmsPanel';
import {
  Building2, Zap, TrendingUp, TrendingDown, AlertTriangle, Activity, Receipt,
  WifiOff, Battery, Leaf, Fuel, CloudOff, TreePine, IndianRupee, Users,
  ChevronDown, ChevronRight, MapPin,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const fmtAgo = (iso) => {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Never';
  const secs = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 5400) return `${Math.round(secs / 60)}m ago`;
  if (secs < 172800) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
};

const STATUS_META = {
  online: { label: 'Online', pill: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  partial_offline: { label: 'Partial', pill: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  offline: { label: 'Offline', pill: 'bg-rose-50 text-rose-700', dot: 'bg-rose-500' },
};

export const DashboardPage = () => {
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, isClient } = useAuth();
  const navigate = useNavigate();

  const fetchFleet = useCallback(async () => {
    try {
      const res = await dashboardAPI.getFleet();
      setFleet(res.data);
    } catch (error) {
      console.error('Failed to fetch fleet dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFleet();
    const interval = setInterval(fetchFleet, 30000);
    return () => clearInterval(interval);
  }, [fetchFleet]);

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div>
      </div>
    );
  }

  const health = fleet?.fleet_health || {};
  const energy = fleet?.energy || {};
  const business = fleet?.business || {};
  const sites = fleet?.sites || [];
  const green = fleet?.sustainability || {};

  const alarmSub = `${formatNumber(health.critical_alarms || 0)} critical · ${formatNumber(health.warning_alarms || 0)} warning`;
  const lifetime = energy.lifetime_discharge_kwh || 0;

  const siteStatusRows = sites.map((s) => ({
    site_id: s.site_id,
    site_name: s.name,
    grid_status: s.grid_connected == null ? 'unknown' : s.grid_connected ? 'up' : 'down',
    soc_percent: s.soc_percent,
    latest_telemetry_at: s.last_data_at,
  }));

  return (
    <div className="flex min-h-screen bg-slate-50/50 overflow-x-hidden">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 overflow-x-hidden">
        <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-2">
          <PageHeader
            title={isClient ? 'Client Portal' : 'Command Center'}
            description={`Welcome back, ${user?.name}. Fleet overview — one source of truth per metric, live.`}
          />

          {/* ── Zone 1: Fleet Health ─────────────────────────────── */}
          <ZoneLabel>Fleet Health</ZoneLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="zone-fleet-health">
            <Tile
              testId="tile-sites-online"
              label="Sites Online"
              value={`${formatNumber(health.sites_online || 0)} / ${formatNumber(health.total_sites || 0)}`}
              sub={`${formatNumber(health.sites_offline || 0)} offline · ${formatNumber(health.sites_partial_offline || 0)} partial`}
              tone={(health.sites_offline || 0) > 0 ? 'red' : 'green'}
              icon={Building2}
              onClick={() => navigate('/sites')}
            />
            <Tile
              testId="tile-active-alarms"
              label="Active Alarms"
              value={formatNumber(health.active_alarms || 0)}
              sub={alarmSub}
              tone={(health.critical_alarms || 0) > 0 ? 'red' : (health.active_alarms || 0) > 0 ? 'amber' : 'green'}
              icon={AlertTriangle}
              onClick={() => navigate('/alarms')}
            />
            <Tile
              testId="tile-data-warnings"
              label="Data Warnings"
              value={formatNumber(health.data_warnings || 0)}
              sub="Sites with stale/missing telemetry"
              tone={(health.data_warnings || 0) > 0 ? 'amber' : 'green'}
              icon={WifiOff}
              onClick={() => navigate('/data-quality')}
            />
            <Tile
              testId="tile-om-required"
              label="O&M Required"
              value={formatNumber(health.om_required_sites || 0)}
              sub={`of ${formatNumber(health.total_sites || 0)} sites need attention`}
              tone={(health.om_required_sites || 0) > 0 ? 'amber' : 'blue'}
              icon={Activity}
              onClick={() => navigate('/alarms')}
            />
          </div>

          {/* ── Zone 2: Energy Performance ───────────────────────── */}
          <ZoneLabel>Energy Performance</ZoneLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="zone-energy">
            <Tile
              testId="tile-discharge-today"
              label="Discharge Today"
              value={`${formatDecimal(energy.discharge_today_kwh || 0, 1)} kWh`}
              sub={`Charge today: ${formatDecimal(energy.charge_today_kwh || 0, 1)} kWh`}
              icon={TrendingDown}
            />
            <Tile
              testId="tile-mtd"
              label="Month-to-Date"
              value={`${formatDecimal(energy.discharge_mtd_kwh || 0, 1)} kWh`}
              sub="Discharged since the 1st (IST)"
              icon={TrendingUp}
            />
            <Tile
              testId="tile-lifetime"
              label="Lifetime Discharge"
              value={lifetime >= 1000
                ? `${formatDecimal(lifetime / 1000, 2)} MWh`
                : `${formatDecimal(lifetime, 1)} kWh`}
              sub="Sum of all site cumulative totals"
              icon={Battery}
            />
            <Tile
              testId="tile-installed"
              label="Installed Fleet Capacity"
              value={`${formatDecimal(energy.installed_power_kw || 0, 0)} kW`}
              sub={`${formatDecimal(energy.installed_capacity_kwh || 0, 0)} kWh across ${formatNumber(health.total_sites || 0)} sites`}
              icon={Zap}
            />
          </div>

          {/* ── Zone 3: Business ─────────────────────────────────── */}
          {!isClient && (
            <>
              <ZoneLabel>Business</ZoneLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="zone-business">
                <Tile
                  testId="tile-total-clients"
                  label="Total Clients"
                  value={formatNumber(business.total_clients || 0)}
                  sub={`Across ${formatNumber(business.clients_with_active_sites || 0)} with active sites`}
                  icon={Users}
                  onClick={() => navigate('/clients')}
                />
                <Tile
                  testId="tile-billing"
                  label="Billing Status"
                  value={formatNumber(business.pending_billing_cycles || 0)}
                  sub="Invoices pending approval"
                  icon={Receipt}
                  onClick={() => navigate('/billing')}
                />
                <Tile
                  testId="tile-total-sites"
                  label="Total Sites"
                  value={formatNumber(business.total_sites || 0)}
                  sub={`${formatNumber(business.total_assets || 0)} assets deployed`}
                  icon={Building2}
                />
                <Tile
                  testId="tile-total-assets"
                  label="Total Assets"
                  value={formatNumber(business.total_assets || 0)}
                  sub="BESS units under management"
                  icon={Battery}
                />
              </div>
            </>
          )}

          {/* ── Unified site status table ─────────────────────────── */}
          <ZoneLabel>Site Status — unified, single source of truth</ZoneLabel>
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="fleet-site-table">
            {sites.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="fleet-site-table-empty">
                No active sites yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                    <tr>
                      {['Site', 'Location', 'Status', 'Grid', 'SoC', 'Load', 'Power / Capacity', 'Last Data'].map((h) => (
                        <th key={h} className="text-left px-4 py-2 text-[10px] font-medium uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sites.map((s) => {
                      const meta = STATUS_META[s.status] || STATUS_META.offline;
                      return (
                        <tr
                          key={s.site_id}
                          className="hover:bg-slate-50/70 cursor-pointer"
                          onClick={() => navigate(`/sites/${s.site_id}`)}
                          data-testid={`fleet-site-row-${s.site_id}`}
                        >
                          <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-1.5">
                            {s.name}
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" />{s.location || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${meta.pill}`}
                              data-testid={`fleet-site-status-${s.site_id}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs font-medium">
                            {s.grid_connected == null
                              ? <span className="text-slate-400">—</span>
                              : s.grid_connected
                                ? <span className="text-emerald-700">Grid UP</span>
                                : <span className="text-rose-700">Grid DOWN</span>}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {s.soc_percent != null ? `${formatDecimal(s.soc_percent, 1)}%` : '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {s.load_kw != null ? `${formatDecimal(s.load_kw, 1)} kW` : '—'}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600">
                            {formatDecimal(s.installed_power_kw, 0)} kW / {formatDecimal(s.installed_capacity_kwh, 0)} kWh
                          </td>
                          <td className="px-4 py-3 text-xs font-mono text-slate-500" data-testid={`fleet-site-lastdata-${s.site_id}`}>
                            {fmtAgo(s.last_data_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Active alarms (same source as the tile above) ─────── */}
          <ZoneLabel>Active Alarms</ZoneLabel>
          <ActiveAlarmsPanel siteStatus={siteStatusRows} />

          {/* ── Environmental impact (secondary, collapsed) ───────── */}
          <details className="bg-white border border-slate-200 rounded-sm mt-4" data-testid="sustainability-section">
            <summary className="px-6 py-4 cursor-pointer flex items-center justify-between list-none">
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 bg-emerald-50 rounded-sm flex items-center justify-center">
                  <Leaf className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block font-heading text-sm font-semibold tracking-tight text-slate-900">
                    Environmental Impact — Sustainability Report
                  </span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">
                    Derived from lifetime discharge ({lifetime >= 1000
                      ? `${formatDecimal(lifetime / 1000, 2)} MWh`
                      : `${formatDecimal(lifetime, 1)} kWh`}) · monthly metric, expand on demand
                  </span>
                </span>
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </summary>
            <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 border-t border-slate-100 pt-5">
              <GreenCard icon={Fuel} iconColor="text-amber-600" iconBg="bg-amber-50" label="Diesel Avoided"
                value={`${formatNumber(green.diesel_avoided_liters || 0)} L`} unit="Litres saved vs DG" />
              <GreenCard icon={CloudOff} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="CO2 Avoided"
                value={`${formatNumber(green.co2_avoided_kg || 0)} kg`} unit="Emissions prevented" />
              <GreenCard icon={IndianRupee} iconColor="text-blue-600" iconBg="bg-blue-50" label="Cost Savings"
                value={formatCurrency(green.cost_savings_inr || 0)} unit="Diesel cost avoided" />
              <GreenCard icon={TreePine} iconColor="text-emerald-600" iconBg="bg-emerald-50" label="Trees Equivalent"
                value={formatNumber(green.trees_equivalent || 0)} unit="Annual CO2 absorption" />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
};

/* ── Helper components ──────────────────────────────────────────── */
function ZoneLabel({ children }) {
  return (
    <div className="flex items-center gap-3 pt-6 pb-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{children}</span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

const TONES = {
  red: 'text-rose-600',
  amber: 'text-amber-600',
  green: 'text-emerald-600',
  blue: 'text-blue-600',
  slate: 'text-slate-900',
};

function Tile({ label, value, sub, tone = 'slate', icon: Icon, onClick, testId }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`bg-white border border-slate-200 rounded-sm p-5 transition-all ${clickable ? 'cursor-pointer hover:border-slate-300 hover:shadow-sm' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
        {clickable
          ? <span className="text-[10px] font-semibold text-blue-600">View →</span>
          : Icon && <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />}
      </div>
      <p className={`font-mono text-2xl font-medium tracking-tight ${TONES[tone] || TONES.slate}`} data-testid={`${testId}-value`}>
        {value}
      </p>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
  );
}

function GreenCard({ icon: Icon, iconColor, iconBg, label, value, unit }) {
  return (
    <div className="border border-slate-100 rounded-sm p-4">
      <div className="flex items-center space-x-2 mb-3">
        <div className={`w-8 h-8 ${iconBg} rounded-sm flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor}`} strokeWidth={1.5} />
        </div>
        <p className="text-xs font-medium text-slate-600">{label}</p>
      </div>
      <p className="font-mono text-xl font-medium tracking-tight text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-400 mt-1">{unit}</p>
    </div>
  );
}
